/**
 * Published pricing.
 *
 * Prices and the contact address come from the deployment's own settings
 * rather than being hard-coded here, so changing a price is a change in one
 * cell — not a rebuild and redeploy of the website.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check } from 'lucide-react';
import { usePublicConfig } from './PublicShell';
import type { PublicCurrencyPrices } from '@shared/types';

/**
 * Which currency to open on, guessed from the browser.
 *
 * The backend cannot do this: an Apps Script web app sees Google's network
 * rather than the visitor's, so every request looks as though it came from the
 * same place. The browser knows its own time zone, which is the least bad
 * signal available without a geolocation service — and it is wrong for
 * travellers and anybody on a VPN, which is why the answer is only ever which
 * button starts selected.
 *
 * Zones rather than `navigator.language`: somebody in Sydney running their
 * laptop in en-GB is common, and a Londoner whose phone says en-AU is not.
 * Only the countries we actually hold prices for are listed — a zone we do not
 * recognise falls through to the base currency, which is the right answer
 * whenever we are unsure.
 */
const ZONE_COUNTRIES: [RegExp, string][] = [
  [/^Australia\//, 'AUD'],
  [/^Pacific\/(Auckland|Chatham)/, 'NZD'],
  [/^Europe\/(London|Belfast|Guernsey|Isle_of_Man|Jersey)/, 'GBP'],
  [/^Europe\/Dublin/, 'EUR'],
  [/^(America|US)\//, 'USD'],
  [/^Canada\//, 'CAD'],
  [/^Africa\/Johannesburg/, 'ZAR'],
  [/^Africa\/Nairobi/, 'KES'],
  [/^Africa\/Lagos/, 'NGN'],
  [/^Asia\/(Kolkata|Calcutta)/, 'INR'],
];

function guessCurrency(available: string[], fallback: string): string {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? '';
    for (const [pattern, currency] of ZONE_COUNTRIES) {
      if (pattern.test(zone) && available.includes(currency)) return currency;
    }
  } catch {
    // An environment with no Intl, or one that refuses the zone. The base
    // currency is the answer either way.
  }
  return fallback;
}

const INCLUDED = [
  'Properties, rooms and utilities',
  'Tenants, leases and occupancy',
  'Rent ledger with a real verification step',
  'Utility bills, split across tenants',
  'Documents, shared only when you choose',
  'Text messages — reminders, receipts and login codes',
  'Tenant portal, on any phone',
  'Maintenance requests, reported to resolved',
  'Staff accounts with four permission levels',
  'Audit trail of every money decision',
];

export default function Pricing() {
  const config = usePublicConfig();
  const [, , custom] = config.pricing.plans;

  const offered: PublicCurrencyPrices[] = config.pricing.currencies?.length
    ? config.pricing.currencies
    : [];
  const codes = offered.map((c) => c.currency);
  const base = config.pricing.currency;

  /*
   * Starts on the base currency and moves once, after mount.
   *
   * The page is prerendered to static HTML at build time, so whatever is
   * rendered first is what a crawler reads and what a link preview shows —
   * and that should be the price this deployment is certain of, in the market
   * it sells in first, rather than a guess made on a build machine.
   */
  const [currency, setCurrency] = useState(base);
  useEffect(() => {
    if (codes.length > 1) setCurrency(guessCurrency(codes, base));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codes.join(','), base]);

  const shown = offered.find((c) => c.currency === currency);
  const starterPrice = shown?.plans.starter.display ?? config.pricing.plans[0]?.price_display ?? null;
  const standardPrice = shown?.plans.standard.display ?? config.pricing.plans[1]?.price_display ?? null;
  const starter = config.pricing.plans[0];
  const standard = config.pricing.plans[1];

  return (
    <div className="mx-auto w-full max-w-5xl px-5 sm:px-6 py-14 sm:py-20">
      <div className="max-w-2xl">
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">Pricing</h1>
        <p className="mt-3 text-muted leading-relaxed">
          One monthly price per organisation. No setup fee, no per-user charge, and no
          per-message billing — text messages are included in the price you see.
        </p>
        <p className="mt-3 text-muted leading-relaxed">
          Every plan starts with a <strong className="text-ink">free trial</strong>. No card
          is needed to begin — set up, add your properties, and decide afterwards.
        </p>
      </div>

      {offered.length > 1 && (
        <div className="mt-8 flex flex-wrap items-center gap-2">
          <span className="text-xs text-faint mr-1">Show prices in</span>
          {offered.map((option) => (
            <button
              key={option.currency}
              type="button"
              onClick={() => setCurrency(option.currency)}
              className={`h-8 px-3 rounded-lg border text-xs font-medium transition-colors ${
                option.currency === currency
                  ? 'border-brand bg-brand/10 text-ink'
                  : 'border-line text-muted hover:text-ink'
              }`}
              aria-pressed={option.currency === currency}
            >
              {option.currency}
            </button>
          ))}
        </div>
      )}

      {shown && !shown.primary && (
        <p className="mt-3 text-xs text-faint leading-relaxed max-w-2xl">
          Prices in {shown.currency} are set from our {base} price and reviewed
          periodically, so they do not move with the exchange rate day to day. You are
          charged in {shown.currency}.
        </p>
      )}

      <div className="mt-10 grid gap-5 md:grid-cols-3 items-start">
        <PlanCard
          name={starter.name}
          price={starterPrice}
          limit={starter.property_limit}
          blurb="For a small portfolio you manage yourself."
          cta={{ to: '/setup?plan=starter', label: 'Start free trial' }}
        />
        <PlanCard
          name={standard.name}
          price={standardPrice}
          limit={standard.property_limit}
          blurb="For a growing portfolio, or a small agency."
          featured
          cta={{ to: '/setup?plan=standard', label: 'Start free trial' }}
        />
        <PlanCard
          name={custom.name}
          price={null}
          limit={null}
          blurb="More than ten properties, or something unusual. We will work it out with you."
          cta={{ href: `mailto:${config.contact.sales}`, label: 'Talk to us' }}
        />
      </div>

      <section className="mt-14">
        <h2 className="text-xl font-semibold tracking-tight">Every plan includes</h2>
        <ul className="mt-5 grid gap-x-8 gap-y-2.5 sm:grid-cols-2">
          {INCLUDED.map((item) => (
            <li key={item} className="flex items-start gap-2.5 text-sm">
              <Check className="h-4 w-4 text-brand shrink-0 mt-0.5" />
              <span className="text-muted">{item}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="mt-14 grid gap-6 sm:grid-cols-2">
        <Note title="Text messages are included">
          Sending texts costs us money, so it is built into the monthly price rather than
          billed on top. There is no per-message charge and no overage invoice, however many
          reminders and receipts you send. If your usage ever became wildly out of step with
          your plan we would talk to you about it first — you would never simply receive a
          larger bill.
        </Note>
        <Note title="Nothing is sent without you">
          PROVIA never texts a tenant on its own. It builds a review queue of who looks
          overdue; you decide who to contact and see the exact wording before it goes.
        </Note>
        <Note title="How the trial ends">
          When the trial finishes, the organisation is blocked until payment is received —
          nothing new can be recorded. Nothing is deleted, and you can still read and
          export everything you entered. Paying unblocks it straight away.
        </Note>
        <Note title="Paying">
          Card, or bank debit where your country supports it. Details are entered on our
          payment provider&rsquo;s own secure page, never on ours — PROVIA never sees or
          stores a card number or a bank account.
        </Note>
        <Note title="Monthly, and cancel when you like">
          Billed monthly in advance. Cancel any time and you keep access to the end of the
          month you have paid for. See the{' '}
          <Link to="/refunds" className="text-brand hover:underline">refunds policy</Link>.
        </Note>
        <Note title="Your data stays yours">
          Close your account and everything is deleted — records and documents alike. See the{' '}
          <Link to="/privacy" className="text-brand hover:underline">privacy policy</Link>.
        </Note>
      </section>

      {config.demo?.email && (
        <div className="mt-14 rounded-2xl border border-brand/30 bg-brand/5 p-6 sm:p-8">
          <h2 className="text-lg font-semibold tracking-tight">Have a look round first</h2>
          <p className="mt-2 text-sm text-muted leading-relaxed">
            A demo account with a real portfolio in it — four tenants, four months of rent,
            one part-paid, one overdue and a payment waiting to be verified. Nothing you do
            in there matters.
          </p>
          <Link
            to="/"
            className="mt-4 inline-block rounded-lg border border-brand/40 px-4 py-2 text-sm font-medium text-brand hover:bg-brand/10 transition-colors"
          >
            Open the demo from the home page →
          </Link>
        </div>
      )}

      <div className="mt-14 rounded-2xl border border-line bg-surface p-6 sm:p-8">
        <h2 className="text-lg font-semibold tracking-tight">Not sure which fits?</h2>
        <p className="mt-2 text-sm text-muted leading-relaxed">
          Tell us how many properties you manage and how you let them, and we will tell you
          straight which plan you need — including if the answer is the cheaper one.
        </p>
        <a
          href={`mailto:${config.contact.sales}`}
          className="mt-4 inline-block rounded-lg bg-brand px-4 py-2 text-sm font-medium text-white hover:opacity-90 transition-opacity"
        >
          {config.contact.sales}
        </a>
      </div>
    </div>
  );
}

function PlanCard({ name, price, limit, blurb, featured, cta }: {
  name: string;
  price: string | null;
  limit: number | null;
  blurb: string;
  featured?: boolean;
  /** Either an in-app route or an external address, never both. */
  cta?: { to?: string; href?: string; label: string };
}) {
  return (
    <div
      className={`rounded-2xl border p-6 ${
        featured ? 'border-brand/50 bg-brand/5' : 'border-line bg-surface'
      }`}
    >
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="font-semibold tracking-tight">{name}</h2>
        {featured && (
          <span className="rounded-full bg-brand/15 text-brand text-xs font-medium px-2 py-0.5">
            Most common
          </span>
        )}
      </div>

      <div className="mt-4 flex items-baseline gap-1.5">
        {price ? (
          <>
            <span className="text-3xl font-semibold tracking-tight">{price}</span>
            <span className="text-sm text-muted">/ month</span>
          </>
        ) : (
          <span className="text-2xl font-semibold tracking-tight">Let&rsquo;s talk</span>
        )}
      </div>

      <p className="mt-1.5 text-sm text-muted">
        {limit ? `Up to ${limit} propert${limit === 1 ? 'y' : 'ies'}` : 'More than 10 properties'}
      </p>

      <p className="mt-4 text-sm text-muted leading-relaxed">{blurb}</p>

      {cta?.to && (
        <Link
          to={cta.to}
          className={`mt-5 block rounded-lg px-4 py-2 text-center text-sm font-medium transition-opacity ${
            featured ? 'bg-brand text-white hover:opacity-90' : 'border border-line hover:bg-raised'
          }`}
        >
          {cta.label}
        </Link>
      )}
      {cta?.href && (
        <a
          href={cta.href}
          className="mt-5 block rounded-lg border border-line px-4 py-2 text-center text-sm font-medium hover:bg-raised transition-colors"
        >
          {cta.label}
        </a>
      )}
    </div>
  );
}

function Note({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-line bg-surface p-5">
      <h3 className="font-medium">{title}</h3>
      <p className="mt-2 text-sm text-muted leading-relaxed">{children}</p>
    </div>
  );
}
